import React, { useState } from "react";
import { Box, Card, CardContent, Typography, Chip, Grid, IconButton, MenuItem, Menu, Avatar } from "@mui/material";
import { format } from "date-fns";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";

import axios from "axios";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { useSelector } from "react-redux";
import CreateEditDialog from "../../../../common/dialog/create-edit-dialog";
import TaskForm from "./tasks-form";
import ReusableDialog from "../../../../common/dialog/dialog";

const ManagerDefaultTaskCard = ({
  tasks,
  onTaskClick,
  refreshTasks,
  setEditOpenSnackbar,
  setDeleteOpenSnackbar,
  staffDebriefs,
  childDebriefs,
  roomSearches,
}) => {
  const user = useSelector((state) => state.auth.user);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [expandedTask, setExpandedTask] = useState(null);

  const handleToggleExpand = (index) => {
    setExpandedTask(expandedTask === index ? null : index);
  };

  const handleMenuOpen = (event, task) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedTask(task);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleEdit = () => {
    handleMenuClose();
    setEditOpen(true);
  };

  const handleClickDelete = () => {
    handleMenuClose();
    setDeleteOpen(true);
  };

  const handleDelete = async (taskId) => {
    if (!taskId) {
      console.error("Task ID is required to delete a task.");
      return;
    }
    try {
      await axios.delete(`/api/assessments/asset/${taskId}`, {
        headers: {
          "Content-Type": "application/json",
          "x-user-role": user?.roleid, // Add your required headers
        },
      });
      setDeleteOpenSnackbar(true); // Show notification on success
      // Close the dialog
      setDeleteOpen(false);
      refreshTasks(); // Refresh tasks after deletion
    } catch (error) {
      console.error("Error deleting task:", error);
    }
  };

  return (
    <Box sx={{ mb: 4 }}>
      {tasks.map((task, index) => {
        return (
          <Card
            sx={{ mb: 2, color: "#ECECED", cursor: "pointer", border: "1px solid #ddd" }}
            onClick={() => {
              onTaskClick(task); // Trigger the task click handler
              handleToggleExpand(index); // Toggle expansion for the task
            }}
          >
            <CardContent>
              <Grid container spacing={1}>
                <Grid item lg={10} md={10}>
                  <Grid container spacing={1} mb={1}>
                    <Grid item>
                      <Typography sx={{ fontSize: "14px", color: "black", fontWeight: "bold" }}>
                        <FiberManualRecordIcon color='info' sx={{ fontSize: 10, mr: 1 }} />
                        {task.taskName}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Typography sx={{ fontSize: "12px", color: "black" }}>{task.description}</Typography>

                  <Grid item>
                    <Chip
                      variant='contained'
                      sx={{
                        fontSize: "12px",
                        borderRadius: 5,
                        backgroundColor: task.taskStatus === "active" && "#EAFFF2",
                        color: task.taskStatus === "active" && "green",
                      }}
                      label={task.taskStatus ?? "inactive"}
                      size='small'
                    />
                  </Grid>
                </Grid>
                <Grid item lg={2} md={2}>
                  <Grid container justifyContent='space-between' alignItems='center'>
                    <Grid container spacing={1}>
                      <Grid item>
                        <Avatar alt='Profile' src={""} className='profile-avatar' sx={{ width: 30, height: 30 }} />
                      </Grid>
                      <Grid item>
                        <Chip
                          variant='contained'
                          sx={{
                            fontSize: "12px",
                            borderRadius: 5,
                            backgroundColor: task.assignee && task.assignee !== "unassigned" ? "#EAFFF2" : "#F2F6FF",
                            color: task.assignee && task.assignee !== "unassigned" ? "green" : "blue",
                          }}
                          label={task.assignee ?? "unassigned"}
                          size='small'
                        />
                      </Grid>
                    </Grid>
                    <Typography sx={{ fontSize: "12px" }}>
                      <br />
                      {task?.timeline === "daily"
                        ? format(new Date(task.created_at), "dd EEE")
                        : `${format(new Date(task.created_at), "dd EEE")} - ${format(
                            new Date(new Date(task.created_at).setDate(new Date(task.created_at).getDate() + 6)),
                            "dd EEE"
                          )}`}
                    </Typography>

                    {task.taskType === "manual" && (
                      <Grid item>
                        {/* Row Actions */}
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation(); // Prevents the click event from propagating to the card's onClick
                            handleMenuOpen(e, task);
                          }}
                        >
                          <MoreVertIcon />
                        </IconButton>
                        <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleMenuClose}>
                          <MenuItem
                            onClick={(e) => {
                              e.stopPropagation(); // Ensures menu actions do not trigger card click
                              handleEdit();
                            }}
                            disabled={selectedTask?.status === "completed"}
                          >
                            <EditIcon fontSize='small' />
                            <Typography sx={{ ml: 1, color: "black" }}>Edit</Typography>
                          </MenuItem>
                          <MenuItem
                            onClick={(e) => {
                              e.stopPropagation(); // Ensures menu actions do not trigger card click
                              handleClickDelete();
                            }}
                            disabled={selectedTask?.status === "completed"}
                          >
                            <DeleteIcon fontSize='small' />
                            <Typography sx={{ ml: 1, color: "black" }}>Delete</Typography>
                          </MenuItem>
                        </Menu>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </CardContent>
            <CreateEditDialog isOpen={editOpen} title='Edit Task'>
              <Box>
                <TaskForm
                  enableReinitialize={true}
                  initialValues={{
                    taskName: selectedTask?.taskName || "",
                    description: selectedTask?.description || "",
                    assignee: selectedTask?.assignee || "",
                    timeline: selectedTask?.timeline || "",
                    checklist: selectedTask?.checklist.map((item) => ({
                      item: item.item || "",
                      status: false,
                    })) || [{ item: "", status: false }],
                    status: "pending",
                    shiftType: selectedTask?.shiftType || "",
                    managed_by: selectedTask?.managed_by || "",
                    taskType: selectedTask?.taskType || "manual",
                    taskStatus: selectedTask?.assignee !== "" ? "active" : selectedTask?.taskStatus,
                    taskOwner: selectedTask?.taskOwner,
                  }}
                  onSubmit={async (values) => {
                    try {
                      await axios.put(`/api/assessments/asset/${selectedTask?.id}`, values, {
                        headers: { "Content-Type": "application/json", "x-user-role": user?.roleid },
                      });

                      setEditOpenSnackbar(true); // Trigger Snackbar
                    } catch (error) {
                      console.error("Error updating task:", error);
                    }
                  }}
                  onClose={() => {
                    setEditOpen(false);
                    refreshTasks();
                  }}
                />
              </Box>
            </CreateEditDialog>
            <ReusableDialog
              isOpen={deleteOpen}
              onClose={() => {
                setDeleteOpen(false);
                refreshTasks(); // Refresh tasks after closing the dialog
              }}
              title='Delete Task'
              submitText='Delete'
              cancelText='Cancel'
              contentText='Are you sure you want to delete this task?'
              onSubmit={() => handleDelete(selectedTask?.id)} // Call the delete function
              maxWidth='md'
            />
          </Card>
        );
      })}
      {roomSearches?.map((roomSearch, index) => {
        return (
          <Card
            sx={{ mb: 2, color: "#ECECED", cursor: "pointer", border: "1px solid #ddd" }}
            onClick={() => {
              onTaskClick(roomSearch); // Trigger the task click handler
              handleToggleExpand(index); // Toggle expansion for the task
            }}
          >
            <CardContent>
              <Grid container spacing={1}>
                <Grid item lg={10} md={10}>
                  <Grid container spacing={1} mb={1}>
                    <Grid item>
                      <Typography sx={{ fontSize: "14px", color: "black", fontWeight: "bold" }}>
                        <FiberManualRecordIcon color='info' sx={{ fontSize: 10, mr: 1 }} />
                        {roomSearch?.taskName}
                      </Typography>
                    </Grid>
                    <Grid item>
                      <Chip
                        variant='contained'
                        sx={{
                          fontSize: "12px",
                          borderRadius: 5,
                        }}
                        label={"inactive"}
                        size='small'
                      />
                    </Grid>
                  </Grid>
                  <Typography sx={{ fontSize: "12px", color: "black" }}>{roomSearch?.description}</Typography>
                </Grid>
                <Grid item lg={2} md={2}>
                  <Grid container justifyContent='space-between' alignItems='center'>
                    <Grid container spacing={1}>
                      <Grid item>
                        <Avatar alt='Profile' src={""} className='profile-avatar' sx={{ width: 30, height: 30 }} />
                      </Grid>
                      <Grid item>
                        <Chip
                          variant='contained'
                          sx={{
                            fontSize: "12px",
                            borderRadius: 5,
                            backgroundColor:
                              roomSearch.assignee && roomSearch.assignee !== "unassigned" ? "#EAFFF2" : "#F2F6FF",
                            color: roomSearch.assignee && roomSearch.assignee !== "unassigned" ? "green" : "blue",
                          }}
                          label={roomSearch.assignee ?? "unassigned"}
                          size='small'
                        />
                      </Grid>
                    </Grid>
                    <Typography sx={{ fontSize: "12px" }}>
                      <br />
                      {roomSearch?.timeline === "daily"
                        ? format(new Date(roomSearch.created_at), "dd EEE")
                        : `${format(new Date(roomSearch.created_at), "dd EEE")} - ${format(
                            new Date(
                              new Date(roomSearch.created_at).setDate(new Date(roomSearch.created_at).getDate() + 2)
                            ),
                            "dd EEE"
                          )}`}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        );
      })}
      {staffDebriefs.map((staffDebrief, index) => {
        return (
          <Card
            sx={{ mb: 2, color: "#ECECED", cursor: "pointer", border: "1px solid #ddd" }}
            onClick={() => {
              onTaskClick(staffDebrief); // Trigger the task click handler
              handleToggleExpand(index); // Toggle expansion for the task
            }}
          >
            <CardContent>
              <Grid container spacing={1}>
                <Grid item lg={10} md={10}>
                  <Grid container spacing={1} mb={1}>
                    <Grid item>
                      <Typography sx={{ fontSize: "14px", color: "black", fontWeight: "bold" }}>
                        <FiberManualRecordIcon color='info' sx={{ fontSize: 10, mr: 1 }} />
                        {staffDebrief?.taskName}
                      </Typography>
                    </Grid>
                    <Grid item>
                      <Chip
                        variant='contained'
                        sx={{
                          fontSize: "12px",
                          borderRadius: 5,
                        }}
                        label={"inactive"}
                        size='small'
                      />
                    </Grid>
                  </Grid>
                  <Typography sx={{ fontSize: "12px", color: "black" }}>{staffDebrief?.description}</Typography>
                </Grid>
                <Grid item lg={2} md={2}>
                  <Grid container justifyContent='space-between' alignItems='center'>
                    <Grid container spacing={1}>
                      <Grid item>
                        <Avatar alt='Profile' src={""} className='profile-avatar' sx={{ width: 30, height: 30 }} />
                      </Grid>
                      <Grid item>
                        <Chip
                          variant='contained'
                          sx={{
                            fontSize: "12px",
                            borderRadius: 5,
                            backgroundColor:
                              staffDebrief.assignee && staffDebrief.assignee !== "unassigned" ? "#EAFFF2" : "#F2F6FF",
                            color: staffDebrief.assignee && staffDebrief.assignee !== "unassigned" ? "green" : "blue",
                          }}
                          label={staffDebrief.assignee ?? "unassigned"}
                          size='small'
                        />
                      </Grid>
                    </Grid>
                    <Typography sx={{ fontSize: "12px" }}>
                      <br />
                      {staffDebrief?.timeline === "daily"
                        ? format(new Date(staffDebrief.created_at), "dd EEE")
                        : `${format(new Date(staffDebrief.created_at), "dd EEE")} - ${format(
                            new Date(
                              new Date(staffDebrief.created_at).setDate(new Date(staffDebrief.created_at).getDate() + 2)
                            ),
                            "dd EEE"
                          )}`}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        );
      })}
      {childDebriefs.map((childDebrief, index) => {
        return (
          <Card
            sx={{ mb: 2, color: "#ECECED", cursor: "pointer", border: "1px solid #ddd" }}
            onClick={() => {
              onTaskClick(childDebrief); // Trigger the task click handler
              handleToggleExpand(index); // Toggle expansion for the task
            }}
          >
            <CardContent>
              <Grid container spacing={1}>
                <Grid item lg={10} md={10}>
                  <Grid container spacing={1} mb={1}>
                    <Grid item>
                      <Typography sx={{ fontSize: "14px", color: "black", fontWeight: "bold" }}>
                        <FiberManualRecordIcon color='info' sx={{ fontSize: 10, mr: 1 }} />
                        {childDebrief?.taskName}
                      </Typography>
                    </Grid>
                    <Grid item>
                      <Chip
                        variant='contained'
                        sx={{
                          fontSize: "12px",
                          borderRadius: 5,
                        }}
                        label={"inactive"}
                        size='small'
                      />
                    </Grid>
                  </Grid>
                  <Typography sx={{ fontSize: "12px", color: "black" }}>{childDebrief?.description}</Typography>
                </Grid>
                <Grid item lg={2} md={2}>
                  <Grid container justifyContent='space-between' alignItems='center'>
                    <Grid container spacing={1}>
                      <Grid item>
                        <Avatar alt='Profile' src={""} className='profile-avatar' sx={{ width: 30, height: 30 }} />
                      </Grid>
                      <Grid item>
                        <Chip
                          variant='contained'
                          sx={{
                            fontSize: "12px",
                            borderRadius: 5,
                            backgroundColor:
                              childDebrief.assignee && childDebrief.assignee !== "unassigned" ? "#EAFFF2" : "#F2F6FF",
                            color: childDebrief.assignee && childDebrief.assignee !== "unassigned" ? "green" : "blue",
                          }}
                          label={childDebrief.assignee ?? "unassigned"}
                          size='small'
                        />
                      </Grid>
                    </Grid>
                    <Typography sx={{ fontSize: "12px" }}>
                      <br />
                      {childDebrief?.timeline === "daily"
                        ? format(new Date(childDebrief.created_at), "dd EEE")
                        : `${format(new Date(childDebrief.created_at), "dd EEE")} - ${format(
                            new Date(
                              new Date(childDebrief.created_at).setDate(new Date(childDebrief.created_at).getDate() + 2)
                            ),
                            "dd EEE"
                          )}`}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        );
      })}
    </Box>
  );
};

export default ManagerDefaultTaskCard;
